import { Source, Status, type SellerLead } from '@smg-real-estate/seller-leads-schema';

import { convertUTCToLocalFormattedString } from '@/helpers/dateUtils';
import { joinNonEmpty } from '@/helpers/SellerLeadsUtils/sellerLeadsUtils';
import {
  LeadSourceToDisplayableSource,
  type DisplayableLead,
  type DisplayableLeadImage,
} from '@/models/sellerLead';

const PLACEHOLDER = '–';

const formatPrice = (price: number): string => {
  const roundedPrice = Math.round(price);
  const formattedPrice = roundedPrice.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1’');
  return 'CHF ' + formattedPrice;
};

const getSplicingAddress = (lead: SellerLead): string => {
  const streetAndHouseNumber = joinNonEmpty([lead.street, lead.houseNumber]);
  const postCodeAndCity = joinNonEmpty([lead.postalCode, lead.city]);
  return joinNonEmpty([streetAndHouseNumber, postCodeAndCity], '\n') || PLACEHOLDER;
};

const getPriceRange = (priceFrom?: number, priceTo?: number): string => {
  if (priceFrom !== undefined && priceTo !== undefined) {
    return `${formatPrice(priceFrom)} – ${formatPrice(priceTo)}`;
  }
  return '';
};

const getPrice = (priceFixed?: number, priceFromSeller?: number): string => {
  if (priceFixed !== undefined || priceFromSeller !== undefined) {
    return formatPrice((priceFixed ?? priceFromSeller)!);
  }
  return '';
};

const isValidImageUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    const imagePattern = /\.(jpg|jpeg|png|gif|bmp|webp)$/i;
    return imagePattern.test(parsedUrl.pathname);
  } catch (error) {
    return false;
  }
};

const getImages = (imageUrls?: string): Array<DisplayableLeadImage> => {
  const imagesArr = imageUrls?.split(',');
  return (imagesArr ?? [])
    .map((url) => url.trim())
    .filter((url) => isValidImageUrl(url))
    .map(
      (url): DisplayableLeadImage => ({
        url: url,
        alt: url.match(/\/([^/]+)\.[^.]+$/)?.[1] || '',
      }),
    );
};

const getDescriptionTitle = (lead: SellerLead): string =>
  [Source.HgPropEvaluation, Source.Is24PropEvaluation].includes(lead.source)
    ? ''
    : (lead.title ?? PLACEHOLDER);

export const updateRefundProperties = (lead: DisplayableLead): DisplayableLead => {
  return {
    ...lead,
    isRefundRequested: lead.status === Status.RefundRequest,
    isRefundAccepted: lead.status === Status.RefundApproved,
    isRefundRefused: lead.status === Status.RefundRefused,
    isRefund: lead.status === Status.RefundRequest || lead.status === Status.RefundApproved,
  };
};

export const adaptData = (lead: SellerLead): DisplayableLead => {
  const displayableLead: DisplayableLead = {
    leadId: lead.leadId,
    propertyType: lead.propertyType,
    address: lead.address,
    shortAddress: joinNonEmpty([lead.postalCode, lead.city]) || PLACEHOLDER,
    name: lead.name,
    phoneNumber: lead.phone,
    phoneVerified: lead.phoneVerified,
    mail: lead.email,
    splicingAddress: getSplicingAddress(lead),
    status: lead.status,
    mandateState: lead.mandateState,
    purchasedDate: convertUTCToLocalFormattedString(lead.purchasedDate, false),
    purchasedDateWithTime: convertUTCToLocalFormattedString(lead.purchasedDate),
    purchasedDateUTC: lead.purchasedDate,
    source: LeadSourceToDisplayableSource[lead.source],
    priceRange: getPriceRange(lead.priceFrom, lead.priceTo) || PLACEHOLDER,
    price: getPrice(lead.priceFixed, lead.priceFromSeller) || PLACEHOLDER,
    livingSpace: (lead.livingSpace ?? PLACEHOLDER).toString(),
    lotSize: (lead.lotSize ?? PLACEHOLDER).toString(),
    rooms: (lead.rooms ?? PLACEHOLDER).toString(),
    title: getDescriptionTitle(lead),
    images: getImages(lead.listOfImages),
    isQualified: lead.isQualified,
    noteForAgency: lead.noteForAgency ?? '',
    selectedAgent: lead.selectedAgent,
    refusedRefundDate: lead.refusedRefundDate
      ? convertUTCToLocalFormattedString(lead.refusedRefundDate)
      : undefined,
    refundRequestDate: lead.refundRequestDate
      ? convertUTCToLocalFormattedString(lead.refundRequestDate)
      : undefined,
    acceptedRefundDate: lead.acceptedRefundDate
      ? convertUTCToLocalFormattedString(lead.acceptedRefundDate)
      : undefined,
    leadSoldPrice: lead.leadSoldPrice?.toString(),
    brokerCommission: (lead.brokerCommission ?? '').toString(),
    commissionShare: (lead.commissionShare ?? '').toString(),
    refundAgencyComment: lead.refundAgencyComment,
    refundReason: lead.refundReason ?? null,
    refundAdminComment: lead.refundAdminComment,
    yearBuild: (lead.yearBuild ?? PLACEHOLDER).toString(),
    isRefundRequested: false,
    isRefundAccepted: false,
    isRefundRefused: false,
    isRefund: false,
  };

  return updateRefundProperties(displayableLead);
};
